import { faStar } from "@fortawesome/pro-regular-svg-icons";
import { faStar as faStarSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faHistory } from "@fortawesome/sharp-light-svg-icons";
import { type ReactNode, useMemo, useRef, useState } from "react";
import type {
    Company,
    EmissionRecordView,
    EventFormInstance,
    Form,
} from "../../../../apiClient/generated";
import { formatDate, formatDateTime } from "../../../../utils/dates";
import { EventFiles } from "../../../EventsV2/EventFiles";
import {
    type FieldDefinition,
    useFormDefinition,
} from "../../../EventsV2/formDefinition";
import { ClosedEventFormActions } from "./FormActions";
import {
    EventFormHeader,
    FormGroupSection,
    FormNavigationPills,
    PillButton,
} from "./FormV2Ui";
import { EventTimeline } from "./TimelineHistory";

interface FormV2ViewOnlyProps {
    emissionData: EmissionRecordView;
    company?: Company;
    availableForms: Form[];
    eventForms: EventFormInstance[];
    onClose: () => void;
}

const extractValue = (
    fieldName: string | null,
    emissionRecord: EmissionRecordView,
): any => {
    if (fieldName === null) return undefined;

    if (fieldName.startsWith("formData.")) {
        const key = fieldName.slice("formData.".length);
        return emissionRecord.mainEvent.formData?.[key];
    }

    switch (fieldName) {
        case "responsible":
            return emissionRecord.mainEvent.responsibleName;
        case "site":
            return emissionRecord.site?.siteName;
        case "equipment":
            return emissionRecord.equipment?.equipmentClass;
        case "extraNotes":
            return emissionRecord.mainEvent.extraNotes;
        case "reviewer":
            return emissionRecord.mainEvent.reviewerName;
        default:
            return undefined;
    }
};

const EventFieldReadOnly = ({
    fieldDef,
    emissionRecord,
}: {
    fieldDef: FieldDefinition;
    emissionRecord: EmissionRecordView;
}) => {
    if (fieldDef.fieldType === "emissionLinking") return null;
    if (
        fieldDef.fieldName === "notifyResponsible" ||
        fieldDef.fieldName === "similarEmission"
    )
        return null;

    const rawValue = extractValue(fieldDef.fieldName, emissionRecord);

    let content: ReactNode;

    switch (fieldDef.fieldType) {
        case "text":
            content = (
                <p>
                    {rawValue != null && rawValue !== ""
                        ? String(rawValue)
                        : "-"}
                </p>
            );
            break;
        case "textarea":
            content = (
                <p className="whitespace-pre-wrap">
                    {rawValue != null && rawValue !== ""
                        ? String(rawValue)
                        : "-"}
                </p>
            );
            break;
        case "number":
            content = <p>{rawValue != null ? rawValue : "-"}</p>;
            break;
        case "select":
            content = (
                <p>
                    {rawValue != null && rawValue !== ""
                        ? String(rawValue)
                        : "-"}
                </p>
            );
            break;
        case "checkbox":
        case "radio":
            content = (
                <p>
                    {rawValue === true
                        ? "Yes"
                        : rawValue === false
                          ? "No"
                          : "-"}
                </p>
            );
            break;
        case "datetime":
            content = (
                <p>{rawValue ? formatDateTime(new Date(rawValue)) : "-"}</p>
            );
            break;
        case "date":
            content = <p>{rawValue ? formatDate(new Date(rawValue)) : "-"}</p>;
            break;
        case "starRating":
            content = (
                <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((score) => (
                        <FontAwesomeIcon
                            key={score}
                            icon={
                                (rawValue || 0) >= score ? faStarSolid : faStar
                            }
                            className="w-5 h-5"
                        />
                    ))}
                </div>
            );
            break;
        case "fileUpload":
            content = (
                <EventFiles event={emissionRecord.mainEvent} readonly={true} />
            );
            break;
        case "user":
            content = (
                <p>
                    {rawValue != null && rawValue !== ""
                        ? String(rawValue)
                        : "-"}
                </p>
            );
            break;
        case "site":
            content = (
                <p>
                    {emissionRecord.site?.siteName ||
                        "Event not associated with my infrastructure"}
                </p>
            );
            break;
        case "equipment":
            content = <p>{emissionRecord.equipment?.equipmentClass || "-"}</p>;
            break;
        default:
            return null;
    }

    return (
        <div>
            <p className="text-sm mb-0.5 text-neutral-500">{fieldDef.label}</p>
            {content}
        </div>
    );
};

export const FormV2ViewOnly = (props: FormV2ViewOnlyProps) => {
    const [showHistory, setShowHistory] = useState(false);

    const formsEnabledForEvent = useMemo(() => {
        const enabledFormIds = props.eventForms.map((ef) => ef.form);
        return props.availableForms.filter((f) =>
            enabledFormIds.includes(f.id),
        );
    }, [props.availableForms, props.eventForms]);

    const formDefinition = useFormDefinition(
        props.company,
        formsEnabledForEvent,
    );

    const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const scrollToGroup = (groupName: string) => {
        groupRefs.current[groupName]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    return (
        <div className="w-full h-full flex flex-col overflow-hidden">
            <div className="flex-shrink-0">
                <EventFormHeader
                    emissionRecord={props.emissionData}
                    requestCloseForm={props.onClose}
                    actions={
                        <ClosedEventFormActions
                            emission={props.emissionData}
                            onClose={props.onClose}
                        />
                    }
                />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto w-full">
                {showHistory && (
                    <div>
                        <div className="flex items-center justify-between p-2 bg-neutral-50 border-b">
                            <h3 className="ml-1">Change history</h3>
                            <PillButton
                                icon={faClose}
                                label="Close History"
                                onClick={() => setShowHistory(false)}
                            />
                        </div>
                        <div className="px-2">
                            <EventTimeline
                                eventId={props.emissionData.mainEvent.id}
                            />
                        </div>
                    </div>
                )}

                {!showHistory && (
                    <>
                        <div className="flex items-center w-full px-2 py-2">
                            <PillButton
                                icon={faHistory}
                                label="Show history"
                                onClick={() => setShowHistory(true)}
                            />
                        </div>

                        <FormNavigationPills
                            forms={formsEnabledForEvent.map((f) => ({
                                form: f,
                            }))}
                            groupsWithErrors={new Set()}
                            onSelectForm={scrollToGroup}
                        />

                        <div className="flex flex-col gap-2 pb-2">
                            {formDefinition.map((group, groupIdx) => (
                                <FormGroupSection
                                    key={group.groupName}
                                    group={group}
                                    defaultOpen={true}
                                    index={groupIdx}
                                    hasError={false}
                                    sectionRef={(el) => {
                                        groupRefs.current[group.groupName] = el;
                                    }}
                                >
                                    {group.fields.map((fieldDef, fieldIdx) => (
                                        <EventFieldReadOnly
                                            key={fieldDef.fieldName ?? fieldIdx}
                                            fieldDef={fieldDef}
                                            emissionRecord={props.emissionData}
                                        />
                                    ))}
                                </FormGroupSection>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
